import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { Observable, Observer, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService } from 'src/app/core/services';
import { environment } from 'src/environments/environment';
import { CommonArrayService } from 'src/app/core/services/common-array.service';

@Component({
  selector: 'app-add-update-push-notifications',
  templateUrl: './add-update-push-notifications.component.html',
  styleUrls: ['./add-update-push-notifications.component.scss'],
})
export class AddUpdatePushNotificationsComponent implements OnInit {
  addUpdateNotifForm: FormGroup;
  idForUpdate: string;
  buttonLoading = false;
  imageLoading = false;
  uploadImageSizeMessage: string | null = null;
  uploadImageSizeLevel: 'normal' | 'success' | 'warning' | 'danger' = 'normal';
  maxImageSizeMB = 1;
  recommendedImageSizeKB = 300;
  goodImageSizeKB = 500;
  tabIndex: number = 0;

  mediaBaseUrl = environment.mediaBaseUrl;
  mediaUploadUrl = environment.apiBaseUrl + '/api/media';

  // ─── Test Notification ───────────────────────────────────────────
  testModeEnabled = false;
  testMobileNumber = '';
  mobileValidationDirty = false;

  // ─── Notification Type ────────────────────────────────────────────
  typeOptions = [
    { label: 'General', value: 'general' },
    { label: 'User', value: 'user' },
    { label: 'Exam Category', value: 'examCategory' },
    { label: 'Plan', value: 'plan' },
  ];

  // ─── Repeat interval ─────────────────────────────────────────────
  repeatDaysTypeOptions = [
    { label: 'No Repeat', value: 'none' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Custom', value: 'custom' },
  ];

  // ─── Target Audience ─────────────────────────────────────────────
  targetAudienceOptions = [
    { label: 'All Users', value: 'all' },
    { label: 'Paid Users', value: 'paid' },
    { label: 'Free Users', value: 'free' },
  ];

  // ─── Target Module (7 sections) ──────────────────────────────────
  moduleList = [
    { label: 'Current Affairs', value: 'currentAffair' },
    { label: 'Test Series', value: 'testSery' },
    { label: 'Editorial Corner', value: 'editorial' },
    { label: 'Plan', value: 'plan' },
    { label: 'Resources', value: 'resource' },
    { label: 'Success Story', value: 'successStory' },
    { label: 'Job Alert', value: 'jobAlert' },
    { label: 'Free Mocks', value: 'freeMock' },
  ];

  // ─── Exam Category (type = examCategory) ─────────────────────────
  examCategoryList: any[] = [];
  examCategoryLoading = false;
  examCategoryTotalCount = 0;
  examCategoryPageIndex = 1;
  examCategoryPageSize = 10;
  examCategorySearchSubject = new Subject<string>();

  // ─── Plan (type = plan) ───────────────────────────────────────────
  planList: any[] = [];
  planLoading = false;
  planTotalCount = 0;
  planPageIndex = 1;
  planPageSize = 10;
  planSearchSubject = new Subject<string>();

  // ─── Module reference items ───────────────────────────────────────
  moduleData: any[] = [];
  moduleLoading = false;
  moduleTotalCount = 0;
  modulePageIndex = 1;
  modulePageSize = 10;
  moduleSearchSubject = new Subject<string>();

  // ─── Audience user list ───────────────────────────────────────────
  userList: any[] = [];
  userLoading = false;
  userTotalCount = 0;
  userPageIndex = 1;
  userPageSize = 10;
  userSearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private msg: NzMessageService,
  ) {
    this.tabIndex = this.activatedRoute.snapshot.queryParams['index'];
    this.idForUpdate = this.activatedRoute.snapshot.params['id'];

    this.addUpdateNotifForm = this.fb.group({
      title: [null, [Validators.required]],
      description: [null],
      image: [null],
      type: ['general', [Validators.required]],
      examCategoryId: [null], // active when type === 'examCategory'
      planId: [null], // active when type === 'plan'
      sendNow: [false],
      scheduleDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      repeatDaysType: ['none'],
      repeatDays: [1, [Validators.min(1)]],
      targetModule: [null],
      referenceId: [null],
      targetAudience: ['all'],
    });
  }

  ngOnInit(): void {
    // sendNow toggles scheduleDate requirement
    this.addUpdateNotifForm
      .get('sendNow')!
      .valueChanges.subscribe((sendNow: boolean) => {
        const ctrl = this.addUpdateNotifForm.get('scheduleDate')!;
        if (sendNow) {
          ctrl.clearValidators();
          ctrl.setValue(null);
        } else {
          ctrl.setValidators([Validators.required]);
        }
        ctrl.updateValueAndValidity();
      });

    this.examCategorySearchSubject.pipe(debounceTime(800)).subscribe((s) => {
      this.examCategoryList = [];
      this.examCategoryPageIndex = 1;
      this.fetchExamCategories(s, 0);
    });

    this.planSearchSubject.pipe(debounceTime(800)).subscribe((s) => {
      this.planList = [];
      this.planPageIndex = 1;
      this.fetchTypePlans(s, 0);
    });

    this.moduleSearchSubject.pipe(debounceTime(800)).subscribe((s) => {
      this.moduleData = [];
      this.modulePageIndex = 1;
      this.fetchModuleData(s, 0);
    });

    this.userSearchSubject.pipe(debounceTime(800)).subscribe((s) => {
      this.userList = [];
      this.userPageIndex = 1;
      this.fetchUserList(s, 0);
    });

    if (this.idForUpdate) {
      this.getNotificationById();
    }
  }

  // ─── Computed getters ─────────────────────────────────────────────
  get isCustomRepeat(): boolean {
    return this.addUpdateNotifForm.get('repeatDaysType')?.value === 'custom';
  }
  get isSendNow(): boolean {
    return !!this.addUpdateNotifForm.get('sendNow')?.value;
  }
  get currentType(): string {
    return this.addUpdateNotifForm.get('type')?.value;
  }
  get currentModule(): string {
    return this.addUpdateNotifForm.get('targetModule')?.value;
  }
  get showTargetModuleSection(): boolean {
    return this.currentType !== 'plan';
  }

  /**
   * The test section is shown only when all required form controls are valid.
   * This excludes the test-specific fields (mobile number, test switch) which
   * are standalone and never submitted with the main form.
   */
  get isFormReadyForTest(): boolean {
    return this.addUpdateNotifForm.valid;
  }

  /**
   * Mobile number validation: exactly 10 digits, no leading zero restriction.
   */
  get isMobileValid(): boolean {
    return /^[6-9]\d{9}$/.test(this.testMobileNumber);
  }

  get mobileErrorTip(): string {
    if (!this.testMobileNumber) {
      return 'Mobile number is required';
    }
    if (!/^\d+$/.test(this.testMobileNumber)) {
      return 'Only digits are allowed';
    }
    if (this.testMobileNumber.length !== 10) {
      return 'Must be exactly 10 digits';
    }
    if (!/^[6-9]/.test(this.testMobileNumber)) {
      return 'Must start with 6, 7, 8, or 9';
    }
    return '';
  }

  // ─── Test Mode ────────────────────────────────────────────────────
  onTestModeToggle(enabled: boolean): void {
    if (!enabled) {
      // Reset test fields when toggled off
      this.testMobileNumber = '';
      this.mobileValidationDirty = false;
    }
  }

  onMobileInput(): void {
    // Strip non-digit characters on the fly
    this.testMobileNumber = this.testMobileNumber.replace(/\D/g, '');
    this.mobileValidationDirty = true;
  }

  /**
   * Navigates to the notification-test route, passing the mobile number
   * and all cleaned-up form data as query params so the backend method
   * at that route can fire the test notification.
   * The mobile number and test switch state are never part of the real submit.
   */
  sendTestNotification(): void {
    this.mobileValidationDirty = true;
    if (!this.isMobileValid) {
      return;
    }

    const raw: any = { ...this.addUpdateNotifForm.value };

    // Apply the same cleanup as the real submit
    if (raw.type !== 'examCategory') delete raw.examCategoryId;
    if (raw.type !== 'plan') delete raw.planId;
    if (raw.repeatDaysType !== 'custom') delete raw.repeatDays;
    if (raw.sendNow) delete raw.scheduleDate;

    this.httpRequestService
      .request('post', 'push-notifications/notification-test', {
        mobile: this.testMobileNumber,
        ...raw,
      })
      .subscribe(
        () => {
          this.notificationService.success(
            'Test Sent',
            `Test notification dispatched to +91${this.testMobileNumber}`,
          );
        },
        (error: any) => {
          const errors: string[] = error.error?.errors
            ? Object.values(error.error.errors)
            : [error.error?.message || 'Failed to send test notification'];
          errors.forEach((e) => this.notificationService.error('', e));
        },
      );
  }

  // ─── Edit mode – fetch by ID ──────────────────────────────────────
  getNotificationById(): void {
    this.httpRequestService
      .request('get', `push-notifications/${this.idForUpdate}`)
      .subscribe(
        (result: any) => {
          const d = result.data;
          this.addUpdateNotifForm.patchValue({
            title: d.title,
            description: d.description,
            image: d.image,
            type: d.type,
            examCategoryId: d.examCategoryId?._id || d.examCategoryId || null,
            planId: d.planId?._id || d.planId || null,
            sendNow: d.sendNow || false,
            scheduleDate: d.scheduleDate ? new Date(d.scheduleDate) : null,
            endDate: d.endDate ? new Date(d.endDate) : null,
            repeatDaysType: d.repeatDaysType || 'none',
            repeatDays: d.repeatDays ?? 0,
            targetModule: d.targetModule,
            referenceId: d.referenceId?._id || d.referenceId || null,
            targetAudience: d.targetAudience || 'all',
          });

          if (d.type === 'examCategory') {
            this.fetchExamCategories('', 0);
          }
          if (d.type === 'plan') {
            this.fetchTypePlans('', 0);
          }
          if (d.targetAudience) {
            this.onAudienceChange(d.targetAudience);
          }
        },
        () => {},
      );
  }

  // ─── Type change ──────────────────────────────────────────────────
  onTypeChange(type: string): void {
    this.addUpdateNotifForm.patchValue({
      examCategoryId: null,
      planId: null,
      targetModule: null,
      referenceId: null,
      targetAudience: 'all',
    });
    this.examCategoryList = [];
    this.planList = [];
    this.moduleData = [];

    if (type === 'examCategory') {
      this.fetchExamCategories('', 0);
    }
    if (type === 'plan') {
      this.fetchTypePlans('', 0);
    }
  }

  onPlanIdChange(planId: string | null): void {
    this.addUpdateNotifForm.patchValue({
      targetModule: planId ? 'plan' : null,
      referenceId: planId ?? null,
    });
  }

  // ─── Exam Categories ──────────────────────────────────────────────
  onExamCategorySearch(s: string): void {
    this.examCategorySearchSubject.next(s);
  }

  loadMoreExamCategories(): void {
    if (this.examCategoryTotalCount > this.examCategoryList.length) {
      this.examCategoryPageIndex++;
      this.fetchExamCategories(
        '',
        this.examCategoryPageSize * (this.examCategoryPageIndex - 1),
      );
    }
  }

  fetchExamCategories(search: string, skip: number): void {
    this.examCategoryLoading = true;
    const params: any = {
      skip,
      limit: this.examCategoryPageSize,
      status: true,
    };
    if (search) params.search = search;

    this.httpRequestService.request('get', 'exam-categories', params).subscribe(
      (result: any) => {
        this.examCategoryLoading = false;
        const incoming = result.data || [];
        this.examCategoryList =
          skip === 0 ? incoming : [...this.examCategoryList, ...incoming];
        this.examCategoryTotalCount = result.totalCount || 0;
      },
      () => {
        this.examCategoryLoading = false;
      },
    );
  }

  // ─── Plans (type = plan) ──────────────────────────────────────────
  onPlanSearch(s: string): void {
    this.planSearchSubject.next(s);
  }

  loadMorePlans(): void {
    if (this.planTotalCount > this.planList.length) {
      this.planPageIndex++;
      this.fetchTypePlans('', this.planPageSize * (this.planPageIndex - 1));
    }
  }

  fetchTypePlans(search: string, skip: number): void {
    this.planLoading = true;
    const params: any = {
      skip,
      limit: this.planPageSize,
      status: true,
      approveStatus: 'approved',
    };
    if (search) params.search = search;

    this.httpRequestService.request('get', 'plans', params).subscribe(
      (result: any) => {
        this.planLoading = false;
        const incoming = result.data || [];
        this.planList = skip === 0 ? incoming : [...this.planList, ...incoming];
        this.planTotalCount = result.totalCount || 0;
      },
      () => {
        this.planLoading = false;
      },
    );
  }

  // ─── Audience ─────────────────────────────────────────────────────
  onAudienceChange(audience: string): void {
    this.userList = [];
    this.userPageIndex = 1;
    this.userTotalCount = 0;
    this.fetchUserList('', 0);
  }

  onUserSearch(s: string): void {
    this.userSearchSubject.next(s);
  }

  loadMoreUsers(): void {
    if (this.userTotalCount > this.userList.length) {
      this.userPageIndex++;
      this.fetchUserList('', this.userPageSize * (this.userPageIndex - 1));
    }
  }

  fetchUserList(search: string, skip: number): void {
    const audience = this.addUpdateNotifForm.get('targetAudience')?.value;
    const examCategoryId = this.addUpdateNotifForm.get('examCategoryId')?.value;

    this.userLoading = true;
    const params: any = { skip, limit: this.userPageSize };
    if (search) params.search = search;
    if (audience === 'paid') params.isPaid = true;
    if (audience === 'free') params.isPaid = false;

    if (this.currentType === 'examCategory' && examCategoryId) {
      params.examCategoryId = examCategoryId;
    }

    this.httpRequestService.request('get', 'users', params).subscribe(
      (result: any) => {
        this.userLoading = false;
        const incoming = result.data || [];
        this.userList = skip === 0 ? incoming : [...this.userList, ...incoming];
        this.userTotalCount = result.totalCount || 0;
      },
      () => {
        this.userLoading = false;
      },
    );
  }

  onExamCategoryChange(): void {
    this.userList = [];
    this.userPageIndex = 1;
    this.userTotalCount = 0;
    this.fetchUserList('', 0);
  }

  // ─── Target Module ────────────────────────────────────────────────
  onModuleChange(module: string): void {
    const referenceControl = this.addUpdateNotifForm.get('referenceId');
    if (module === 'testSery') {
      referenceControl?.setValidators([Validators.required]);
    } else {
      referenceControl?.clearValidators();
    }
    referenceControl?.updateValueAndValidity();

    this.addUpdateNotifForm.patchValue({ referenceId: null });
    this.moduleData = [];
    this.modulePageIndex = 1;
    this.moduleTotalCount = 0;
    if (module) {
      this.fetchModuleData('', 0);
    }
  }

  onModuleSearch(s: string): void {
    this.moduleSearchSubject.next(s);
  }

  loadMoreModuleData(): void {
    if (this.moduleTotalCount > this.moduleData.length) {
      this.modulePageIndex++;
      this.fetchModuleData(
        '',
        this.modulePageSize * (this.modulePageIndex - 1),
      );
    }
  }

  fetchModuleData(search: string, skip: number): void {
    const module = this.addUpdateNotifForm.get('targetModule')?.value;
    if (!module) return;

    this.moduleLoading = true;
    const params: any = { skip, limit: this.modulePageSize };
    if (search) params.search = search;

    const moduleConfig: Record<
      string,
      { url: string; extra?: Record<string, any> }
    > = {
      currentAffair: {
        url: 'current-affairs',
        extra: { status: true, approveStatus: 'approved' },
      },
      testSery: {
        url: 'test-series/for-list',
        extra: { status: true, approveStatus: 'approved' },
      },
      editorial: {
        url: 'editorials',
        extra: { status: true, approveStatus: 'approved' },
      },
      plan: {
        url: 'plans',
        extra: { status: true, approveStatus: 'approved' },
      },
      resource: {
        url: 'resources',
        extra: { status: true, approveStatus: 'approved' },
      },
      successStory: {
        url: 'success-stories',
        extra: { status: true, approveStatus: 'approved' },
      },
      jobAlert: {
        url: 'job-alerts',
        extra: {
          status: true,
          approveStatus: 'approved',
          publishedStatus: 'published',
        },
      },
    };

    const cfg = moduleConfig[module];
    if (!cfg) {
      this.moduleLoading = false;
      return;
    }
    Object.assign(params, cfg.extra || {});

    this.httpRequestService.request('get', cfg.url, params).subscribe(
      (result: any) => {
        this.moduleLoading = false;
        const incoming = result.data || [];
        this.moduleData =
          skip === 0 ? incoming : [...this.moduleData, ...incoming];
        this.moduleTotalCount = result.totalCount || 0;
      },
      () => {
        this.moduleLoading = false;
      },
    );
  }

  getModuleLabel(module: string): string {
    return this.moduleList.find((m) => m.value === module)?.label || 'Item';
  }

  getModuleItemLabel(module: string, item: any): string {
    switch (module) {
      case 'currentAffair':
      case 'editorial':
      case 'successStory':
      case 'jobAlert':
      case 'resource':
        return item.title || item.name || item._id;
      default:
        return item.name || item.title || item._id;
    }
  }

  // ─── Submit ───────────────────────────────────────────────────────
  submit(): void {
    if (!this.addUpdateNotifForm.valid) {
      this.markFormGroupTouched(this.addUpdateNotifForm);
      return;
    }
    const method = this.idForUpdate ? 'put' : 'post';
    const url = this.idForUpdate
      ? `push-notifications/${this.idForUpdate}`
      : 'push-notifications';
    const msg = this.idForUpdate
      ? 'Notification Successfully Updated'
      : 'Notification Added Successfully';
    this.addOrUpdateNotification(method, url, msg);
  }

  addOrUpdateNotification(
    method: string,
    url: string,
    successMsg: string,
  ): void {
    this.buttonLoading = true;
    const raw: any = { ...this.addUpdateNotifForm.value };

    // Strip type-specific fields not relevant to the current type
    if (raw.type !== 'examCategory') delete raw.examCategoryId;
    if (raw.type !== 'plan') delete raw.planId;
    if (raw.repeatDaysType !== 'custom') delete raw.repeatDays;
    if (raw.sendNow) delete raw.scheduleDate;

    // Ensure test-only fields are never submitted
    // (they are standalone NgModel fields, not part of the FormGroup,
    //  so raw won't contain them — this is just a safety guard)
    delete raw.testMobileNumber;
    delete raw.isTest;

    this.httpRequestService.request(method, url, raw).subscribe(
      () => {
        this.notificationService.success('', successMsg);
        this.router.navigate(['/main/push-notifications'], {
          queryParams: { index: this.tabIndex },
        });
        this.buttonLoading = false;
      },
      (error: any) => {
        const errors: string[] = error.error?.errors
          ? Object.values(error.error.errors)
          : [error.error?.message || 'Something went wrong'];
        errors.forEach((e) => this.notificationService.error('', e));
        this.buttonLoading = false;
      },
    );
  }

  // ─── Image Upload ─────────────────────────────────────────────────
  customRequestHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  private formatBytes(bytes: number): string {
    if (bytes === 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB'];
    const exponent = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, exponent);
    return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
  }

  UploadImageFile(info: { file: NzUploadFile }): void {
    switch (info.file.status) {
      case 'uploading':
        this.imageLoading = true;
        break;
      case 'done':
        this.addUpdateNotifForm.patchValue({
          image: info.file.response.data.path,
        });
        this.imageLoading = false;
        break;
      case 'error':
        this.msg.error('Network error');
        this.imageLoading = false;
        break;
    }
  }

  beforeImageUpload = (file: NzUploadFile): Observable<any> =>
    new Observable((observer: Observer<boolean>) => {
      this.uploadImageSizeMessage = null;
      this.uploadImageSizeLevel = 'normal';

      const allowed = [
        'image/jpg',
        'image/jpeg',
        'image/png',
        'image/JPG',
        'image/JPEG',
        'image/PNG',
        'image/webp',
      ];
      if (!allowed.includes(file.type!)) {
        this.msg.error('You can only upload image files!');
        return observer.complete();
      }

      const fileSizeBytes = file.size || 0;
      const fileSizeKB = fileSizeBytes / 1024;
      const fileSizeMB = fileSizeKB / 1024;
      const fileSizeLabel = this.formatBytes(fileSizeBytes);

      if (fileSizeMB > this.maxImageSizeMB) {
        this.uploadImageSizeLevel = 'danger';
        this.uploadImageSizeMessage =
          `Selected image size: ${fileSizeLabel}. ` +
          `Maximum allowed size is 1 MB. Please optimize the image and try again.`;
        return observer.complete();
      }

      if (fileSizeKB > this.goodImageSizeKB) {
        this.uploadImageSizeLevel = 'warning';
        this.uploadImageSizeMessage =
          `Selected image size: ${fileSizeLabel}. ` +
          `This is still acceptable, but it is larger than the recommended target of 300–600 KB.`;
        observer.next(true);
        observer.complete();
        return;
      }

      if (fileSizeKB > this.recommendedImageSizeKB) {
        this.uploadImageSizeLevel = 'success';
        this.uploadImageSizeMessage =
          `Selected image size: ${fileSizeLabel}. ` +
          `Good size — keep it under 600 KB for optimal performance.`;
        observer.next(true);
        observer.complete();
        return;
      }

      this.uploadImageSizeLevel = 'normal';
      this.uploadImageSizeMessage = `Selected image size: ${fileSizeLabel}. Ideal size is 300 KB or less.`;
      observer.next(true);
      observer.complete();
    });

  removeImage(): void {
    const path = this.addUpdateNotifForm.value.image;
    this.addUpdateNotifForm.patchValue({ image: null });
    this.uploadImageSizeMessage = null;
    this.uploadImageSizeLevel = 'normal';
    if (path) {
      this.httpRequestService
        .request('post', 'media/delete-media', { path })
        .subscribe(() =>
          this.notificationService.success('', 'Image Deleted Successfully'),
        );
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      formGroup.controls[key].markAsDirty();
      formGroup.controls[key].updateValueAndValidity();
    });
  }
}
